/* eslint-disable camelcase */
/** @jsxImportSource @emotion/react */
import {
  Auth0Client,
  createAuth0Client,
  IdToken,
  User,
} from '@auth0/auth0-spa-js';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import { LocalStorageKey, PageAction, PageRoute } from 'enums';
import { Constants } from 'global';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasAuth0Error, logError } from 'utils';

interface Auth0ProviderProps {
  children: React.ReactNode;
  domain: string;
  clientId: string;
  redirectUri: string;
}

interface Auth0ContextProps {
  isAuthenticated: boolean;
  user: unknown;
  accessToken: string;
  loading: boolean;
  popupOpen: boolean;
  loginWithPopup: ({}: unknown) => unknown;
  handleRedirectCallback: ({}: unknown) => unknown;
  getIdTokenClaims: ({}: unknown) => unknown;
  loginWithRedirect: ({}: unknown) => unknown;
  getTokenSilently: ({}: unknown) => Promise<string>;
  getTokenWithPopup: ({}: unknown) => Promise<string>;
  logout: () => void;
}

export const Auth0Context = React.createContext<Auth0ContextProps>(null);
export const useAuth0 = (): Auth0ContextProps => React.useContext(Auth0Context);

export const Auth0Provider: React.FC<Auth0ProviderProps> = ({
  children,
  domain,
  clientId,
  redirectUri,
}) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState<boolean>();
  const [user, setUser] = React.useState<User>();
  const [accessToken, setAccessToken] = React.useState<string>();
  const [auth0Client, setAuth0] = React.useState<Auth0Client>();
  const [loading, setLoading] = React.useState(true);
  const [popupOpen, setPopupOpen] = React.useState(false);

  const { getData } = useVisitorData({}, { immediate: false });
  const INVALIDFPJSID = 0;

  const navigate = useNavigate();

  const onRedirectCallback = (): void => {
    const referringUrl =
      localStorage.getItem(LocalStorageKey.ReferringUrl) ?? PageRoute.Home;

    navigate(referringUrl);

    localStorage.removeItem(LocalStorageKey.ReferringUrl);
  };

  const setCredentials = async (): Promise<void> => {
    const token = await auth0Client.getTokenSilently({});
    setAccessToken(token);
    const auth0User = await auth0Client.getUser();
    setUser(auth0User);
  };

  React.useEffect(() => {
    const initAuth0 = async (): Promise<void> => {
      const auth0FromHook = await createAuth0Client({
        domain,
        clientId,
        cacheLocation: 'memory',
        useRefreshTokens: false,
        authorizationParams: {
          redirect_uri: redirectUri,
          scope: `${process.env.REACT_APP_AUTH0_SCOPE}`,
          audience: `${process.env.REACT_APP_AUTH0_AUDIENCE}`,
        },
      });
      setAuth0(auth0FromHook);

      const passwordHasExpired = hasAuth0Error(
        Constants.errors.passwordHasExpired
      );
      const suspiciousLogin = hasAuth0Error(Constants.errors.suspiciousLogin);

      if (
        window.location.search.includes('code=') &&
        !passwordHasExpired &&
        !suspiciousLogin
      ) {
        await auth0FromHook.handleRedirectCallback(window.location.href);
        onRedirectCallback();
      }

      const isAuthed =
        passwordHasExpired || suspiciousLogin
          ? false
          : await auth0FromHook.isAuthenticated();
      setIsAuthenticated(isAuthed);

      setLoading(false);
    };

    initAuth0();
  }, []);

  React.useEffect(() => {
    if (!loading && isAuthenticated) {
      setCredentials();
    }
  }, [loading, isAuthenticated]);

  const loginWithPopup = async (params = {}): Promise<void> => {
    setPopupOpen(true);
    try {
      await auth0Client?.loginWithPopup(params);
    } catch (err) {
      logError(PageAction.auth0Login, err, { params });
    } finally {
      setPopupOpen(false);
    }
    const auth0User = await auth0Client.getUser();
    setUser(auth0User);
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async (): Promise<void> => {
    setLoading(true);
    await auth0Client?.handleRedirectCallback();
    onRedirectCallback();
    setLoading(false);
    setIsAuthenticated(true);
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        accessToken,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        getIdTokenClaims: (): Promise<IdToken> =>
          auth0Client?.getIdTokenClaims(),
        loginWithRedirect: (...p): void => {
          localStorage.setItem(
            LocalStorageKey.ReferringUrl,
            window.location.pathname
          );
          if (process.env.REACT_APP_ENABLE_FPJS === 'true') {
            // If data (visitorId AND requestId) is missing due to package failure/API limit reached, MFA is skipped
            getData()
              .then((data) => {
                if (data) {
                  auth0Client?.loginWithRedirect({
                    authorizationParams: {
                      visitorId: data.visitorId,
                      requestId: data.requestId,
                    },
                  });
                } else {
                  auth0Client?.loginWithRedirect({
                    authorizationParams: {
                      visitorId: INVALIDFPJSID,
                      requestId: INVALIDFPJSID,
                    },
                  });
                }
              })
              .catch((err) => {
                logError(PageAction.auth0Login, err);
                auth0Client?.loginWithRedirect({
                  authorizationParams: {
                    visitorId: INVALIDFPJSID,
                    requestId: INVALIDFPJSID,
                  },
                });
              });
          } else {
            auth0Client?.loginWithRedirect(...p);
          }
        },
        getTokenSilently: async (options?: any): Promise<string> => {
          const token = await auth0Client?.getTokenSilently({
            ...options,
            authorizationParams: {
              ...options?.authorizationParams,
              scope: `${process.env.REACT_APP_AUTH0_SCOPE}`,
              audience: `${process.env.REACT_APP_AUTH0_AUDIENCE}`,
            },
          });
          return token as unknown as string;
        },
        getTokenWithPopup: (...p): Promise<string> =>
          auth0Client?.getTokenWithPopup(...p),
        logout: (): void | Promise<void> =>
          auth0Client?.logout({
            logoutParams: {
              returnTo: window.location.origin,
            },
          }),
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};

interface RequireUserProps {
  children: React.ReactNode;
}

export const requireUser = (page: React.FC): React.FC<RequireUserProps> => {
  return (props: unknown): React.FunctionComponentElement<unknown> => {
    const location = useLocation();
    const { loading, isAuthenticated, loginWithRedirect } = useAuth0();

    React.useEffect(() => {
      if (loading || isAuthenticated) {
        return;
      }

      const fn = async (): Promise<void> => {
        await loginWithRedirect({
          appState: { targetUrl: location.pathname },
        });
      };
      fn();
    }, [loading, isAuthenticated, loginWithRedirect, location.pathname]);

    if (loading || !isAuthenticated) {
      return null;
    }

    return React.createElement(page, props);
  };
};
